import { Link } from "react-router-dom";
import StatusTag from "@/components/feature/StatusTag";
import {
  CORRECTION_EMAIL,
  formatDate,
  formatHeat,
  getCategoryLabel,
  getRelatedTopics,
} from "@/lib/site";
import type { Topic } from "@/types/content";

const tocItems = [
  { id: "overview", label: "事件综述" },
  { id: "keypoints", label: "关键节点" },
  { id: "timeline", label: "完整时间线" },
  { id: "records", label: "记录说明" },
];

export default function TopicSidebar({ topic }: { topic: Topic }) {
  const related = getRelatedTopics(topic, 3);

  return (
    <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="font-heading text-sm font-bold uppercase tracking-[0.16em] text-foreground-500">
          档案信息
        </h2>
        <dl className="mt-4 space-y-3 text-sm">
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">所属分类</dt>
            <dd className="font-medium text-foreground-900">
              {getCategoryLabel(topic.categoryId)}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">当前状态</dt>
            <dd>
              <StatusTag label={topic.status} tone={topic.statusTone} />
            </dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">进展节点</dt>
            <dd className="font-medium text-foreground-900">{topic.timeline.length} 个</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">热度值</dt>
            <dd className="font-medium text-foreground-900">{formatHeat(topic.heat)}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">最近更新</dt>
            <dd className="font-medium text-foreground-900">{formatDate(topic.updatedAt)}</dd>
          </div>
        </dl>
      </div>

      <nav
        aria-label="本页目录"
        className="rounded-lg border border-background-200 bg-background-100 p-5"
      >
        <h2 className="font-heading text-sm font-bold uppercase tracking-[0.16em] text-foreground-500">
          本页目录
        </h2>
        <ul className="mt-4 space-y-2">
          {tocItems.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className="flex items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-600"
              >
                <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true" />
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="font-heading text-sm font-bold uppercase tracking-[0.16em] text-foreground-500">
          相关档案
        </h2>
        <ul className="mt-4 space-y-4">
          {related.map((item) => (
            <li key={item.id}>
              <Link to={`/topic/${item.id}`} className="group block">
                <span className="block font-heading text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                  {item.title}
                </span>
                <span className="mt-1 block text-xs text-foreground-500">
                  {getCategoryLabel(item.categoryId)} · {item.status}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
        <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-primary-800">
          <i className="ri-error-warning-line" aria-hidden="true" />
          发现内容有误？
        </h2>
        <p className="mt-2 text-xs leading-relaxed text-primary-800/80">
          如果你掌握更准确的公开信息，欢迎来信说明。经核对后会更新档案，并在时间线中标注更正记录。
        </p>
        <a
          href={`mailto:${CORRECTION_EMAIL}`}
          className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-mail-line" aria-hidden="true" />
          提交纠错
        </a>
      </div>
    </aside>
  );
}